import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { ApiError, api, onUnauthorized } from './api/client';
import type { AdminSession } from './api/types';

const ME_KEY = ['auth', 'me'] as const;

type AuthValue = {
  session: AdminSession | null;
  loading: boolean;
  error: ApiError | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<AdminSession | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<AdminSession>('/admin-api/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  useEffect(
    () =>
      onUnauthorized(() => {
        queryClient.setQueryData(ME_KEY, null);
        queryClient.removeQueries({ queryKey: ['api'] });
      }),
    [queryClient],
  );

  const login = useCallback(
    async (username: string, password: string) => {
      const session = await api.post<AdminSession>('/admin-api/auth/login', { username, password });
      queryClient.setQueryData(ME_KEY, session);
      await queryClient.invalidateQueries({ queryKey: ['api'] });
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/admin-api/auth/logout');
    } finally {
      queryClient.setQueryData(ME_KEY, null);
      queryClient.removeQueries({ queryKey: ['api'] });
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({
      session: meQuery.data ?? null,
      loading: meQuery.isLoading,
      error: meQuery.error ?? null,
      login,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, meQuery.error, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
